import { useMemo, useState, type CSSProperties } from 'react';

import { useOrgTree } from '@/lib/orgTree';

/** 목록에 한 번에 보이는 사람 수. 더 보려면 더 적는다. */
const SHOWN = 8;

/** 한 칸에 여러 사람을 적을 때 쓰는 구분자 — 「김안전 · 이점검」. 찾는 것은 마지막 조각이다. */
const SEPARATOR = /[·,]/;

type Person = { userId: string; name: string; hint: string };

/**
 * 강사 · 점검자 칸(사용자 지시 2026-10-20) — <b>적는 칸</b>이고, 적는 중인 이름이 우리 임직원과 맞으면
 * 목록이 떠서 고를 수 있다.
 *
 * <p>고르기만 받는 칸({@code PersonnelSearchInput})으로 두지 않는다 — 외부 강사(공단 · 기관)도 적어야 한다.
 * 그래서 값은 글자 그대로 저장되고, 직원과 이어 두지 않는다. 고르면 그 사람의 이름이 들어갈 뿐이다.
 *
 * <p>직원 목록은 결재 조직도({@code /approvals/org-tree})에서 받는다. 전자결재 조회는 전 직원에게 열려
 * 있어 안전보건팀에 인사 권한이 없어도 받는다. 겸직이면 두 조직에 다 나오므로 계정으로 한 번만 세운다.
 */
export function NameSuggestInput({ value, onChange, style }: {
  value: string;
  onChange: (value: string) => void;
  style?: CSSProperties;
}) {
  const org = useOrgTree();
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);

  const people = useMemo(() => {
    const byId = new Map<string, Person>();
    for (const unit of org.data ?? []) {
      for (const m of unit.members) {
        if (byId.has(m.userId)) continue;
        byId.set(m.userId, { userId: m.userId, name: m.name, hint: [unit.unitName, m.position].filter(Boolean).join(' · ') });
      }
    }
    return [...byId.values()];
  }, [org.data]);

  // 마지막 조각만 찾는다 — 앞에 적어 둔 사람은 건드리지 않는다.
  const parts = value.split(SEPARATOR);
  const needle = (parts[parts.length - 1] ?? '').replace(/\s+/g, '');
  const matches = useMemo(
    () => (needle ? people.filter((p) => p.name.replace(/\s+/g, '').includes(needle)).slice(0, SHOWN) : []),
    [needle, people],
  );
  // 이미 그 이름을 다 적었으면 목록을 띄우지 않는다 — 적을 때마다 같은 한 줄이 따라다니면 거슬린다.
  const exact = matches.length === 1 && matches[0]!.name.replace(/\s+/g, '') === needle;
  const shown = open && matches.length > 0 && !exact;

  const pick = (person: Person) => {
    const head = value.slice(0, value.length - (parts[parts.length - 1] ?? '').length);
    // 구분자 뒤에는 한 칸 띄운다(「김안전 · 이점검」).
    onChange(head ? `${head.replace(/\s*$/, '')} ${person.name}` : person.name);
    setOpen(false);
  };

  return (
    <div style={{ position: 'relative' }}>
      <input
        className="input"
        style={style}
        value={value}
        onFocus={() => setOpen(true)}
        onBlur={() => setOpen(false)}
        onChange={(e) => {
          onChange(e.target.value);
          setActive(0);
          setOpen(true);
        }}
        onKeyDown={(e) => {
          if (!shown) return;
          if (e.key === 'ArrowDown') {
            e.preventDefault();
            setActive((i) => Math.min(i + 1, matches.length - 1));
          } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setActive((i) => Math.max(i - 1, 0));
          } else if (e.key === 'Enter') {
            e.preventDefault();
            pick(matches[Math.min(active, matches.length - 1)]!);
          } else if (e.key === 'Escape') {
            // 모달까지 닫히지 않게 — 목록만 접는다.
            e.stopPropagation();
            setOpen(false);
          }
        }}
      />
      {shown && (
        <div
          role="listbox"
          style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            // 칸이 좁으면(점검자) 소속 · 직책이 잘린다 — 칸보다 넓어지게 두되 끝없이 퍼지지는 않게.
            minWidth: '100%',
            width: 'max-content',
            maxWidth: '360px',
            zIndex: 20,
            marginTop: '2px',
            border: '1px solid var(--border)',
            borderRadius: '6px',
            background: 'var(--surface, #fff)',
            boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
            maxHeight: '220px',
            overflowY: 'auto',
          }}
        >
          {matches.map((person, i) => (
            <button
              key={person.userId}
              type="button"
              role="option"
              aria-selected={i === active}
              // 누르는 순간 칸의 blur 가 목록을 닫지 않도록 — 닫히면 click 이 갈 곳이 없다.
              onMouseDown={(e) => e.preventDefault()}
              onMouseEnter={() => setActive(i)}
              onClick={() => pick(person)}
              style={{
                display: 'block',
                width: '100%',
                textAlign: 'left',
                padding: '6px 10px',
                fontSize: '13px',
                background: i === active ? 'var(--surface-hover, #f1f5f9)' : 'none',
                border: 'none',
                cursor: 'pointer',
                whiteSpace: 'nowrap',
              }}
            >
              {person.name}
              {person.hint && <span style={{ color: 'var(--text-muted)', fontSize: '12px' }}> {person.hint}</span>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
